import React from 'react';
import PropTypes from 'prop-types';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography
} from '@one-thd/sui-atomic-components';
import { LinkRefinement } from '../Refinements/CategoryRefinement/LinkRefinement';
import { getInitialItemsToShowCount } from '../../utils/showMoreLessUtility';
import { useShowMoreLess } from '../../hooks/useShowMoreLess';

/**
 * Renders category breadcrumbs, current category heading, and refinement links via LinkRefinement.
 * Supports nested subcategories via NestedLinkRefinement.
 */

const LinkDimension = ({
  breadCrumbs,
  dimension,
  onChange
}) => {

  const getRefinementId = (refinement, refinementIndex) => {
    const { refinementKey } = refinement;
    return `${refinementKey}-${refinementIndex}`;
  };

  const breadCrumbsCategories = Array.isArray(breadCrumbs)
    ? breadCrumbs.filter((breadCrumb) => (breadCrumb?.dimensionName === 'Category'))
    : [];

  const activeCategory = breadCrumbsCategories[breadCrumbsCategories.length - 1];
  const previousCategories = breadCrumbsCategories.slice(0, -1);

  const { label: categoryLabel = '', url: categoryUrl = '' } = activeCategory || {};
  const { refinements = [] } = dimension || {};

  const hasCategoryLabel = Boolean(categoryLabel?.trim());

  const initialCount = getInitialItemsToShowCount(dimension, refinements);
  const {
    visibleItems: visibleRefinements,
    toggleLabel: refinementToggleLabel,
    handleToggle: handleToggleRefinements,
    shouldShowToggle
  } = useShowMoreLess(refinements, initialCount);

  return (
    <div
      className="sui-mb-3"
      data-testid="categoryDrawerFilter"
    >
      {previousCategories?.length > 0 && (
        <List disablePadding>
          {previousCategories.map((category, categoryIndex) => {
            const id = getRefinementId(category, categoryIndex);
            return (
              <LinkRefinement
                arrow
                dimension={dimension}
                id={id}
                key={id}
                onChange={onChange}
                refinement={category}
              />
            );
          })}
        </List>
      )}
      {
        hasCategoryLabel && (
          <div className="sui-mt-2 sui-mb-2" data-testid={`Category-Label-${categoryLabel}`}>
            {/* SEO-friendly hidden link - Required for SEO significance. Reach out to SEO team if wanting to change */}
            <a
              href={categoryUrl}
              className="sui-hidden"
              aria-hidden="true"
              tabIndex="-1"
            >
              {/* H3 element required for SEO significance. Reach out to SEO team if wanting to change */}
              <h3>{categoryLabel}</h3>
            </a>

            {/* Interactive visible element */}
            <Typography
              variant="body-base"
              weight="bold"
              onClick={(event) => {
                event.preventDefault();
                if (onChange) {
                  onChange({ refinement: { label: categoryLabel, url: categoryUrl }, dimension });
                }
              }}
            >
              {categoryLabel}
            </Typography>
          </div>
        )
      }
      {visibleRefinements?.length > 0 && (
        <div className="sui-overflow-y-auto">
          <List disablePadding>
            {visibleRefinements.map((refinement, refinementIndex) => {
              const id = getRefinementId(refinement, refinementIndex);
              return (
                <LinkRefinement
                  dimension={dimension}
                  id={id}
                  key={id}
                  onChange={onChange}
                  refinement={refinement}
                  hasBoldStyle={!hasCategoryLabel && refinement.nestedRefinements !== null}
                  isBreadCrumbPresent={breadCrumbsCategories.length > 0}
                />
              );
            })}
          </List>
        </div>
      )}
      {shouldShowToggle && (
        <List disablePadding>
          <ListItem>
            <ListItemButton
              data-testid="refinement-category-toggle-view-more"
              aria-label={refinementToggleLabel}
              onClick={handleToggleRefinements}
            >
              <ListItemText
                primary={refinementToggleLabel}
              />
            </ListItemButton>
          </ListItem>
        </List>
      )}
    </div>
  );
};

LinkDimension.propTypes = {
  breadCrumbs: PropTypes.arrayOf(
    PropTypes.shape({})
  ).isRequired,
  dimension: PropTypes.shape({
    dimensionId: PropTypes.string,
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    )
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

export { LinkDimension };